import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Chip, IconButton, Tooltip } from '@mui/material';
import { createContext, useContext } from 'react';
import { displayStatus, formatDue, languageKey, shortName, STATUS_META, videoFailed } from './taskUi';

/**
 * Task info for the Files browser: { index: { contentId: { languageKey: row } }, openTask }.
 * Default is empty, so file rows look exactly as before when nothing is provided.
 */
export const TaskIndicatorsContext = createContext({ index: {}, openTask: null });

/** Marks a video in the Files browser that failed to process and can't open. */
export const VideoFailedChip = ({ file }) => {
    if (!videoFailed(file)) return null;
    return (
        <Tooltip title="This video failed to process" arrow>
            <Chip label="Failed" size="small" color="error" variant="outlined" sx={{ fontSize: '0.65rem', height: '18px' }} />
        </Tooltip>
    );
};

/** Marks an audio file in the Files browser whose dubbing failed. Details shows why. */
export const AudioFailedChip = ({ file }) => {
    if (file?.dubbingStatus !== 'FAILED') return null;
    return (
        <Tooltip title="Dubbing failed. Open Details to see why." arrow>
            <Chip label="Failed" size="small" color="error" variant="outlined" sx={{ fontSize: '0.65rem', height: '18px' }} />
        </Tooltip>
    );
};

/**
 * A small stage indicator next to a language chip, with its own click target that opens the
 * task drawer. Renders nothing when the file+language has no task.
 */
export const TaskChipIndicator = ({ contentId, language }) => {
    const { index, openTask } = useContext(TaskIndicatorsContext);
    const key = languageKey(language);
    const row = key && index?.[contentId]?.[key];
    if (!row || !openTask) return null;
    const status = displayStatus(row);
    const Icon = status === 'BLOCKED' ? BlockIcon : status === 'OVERDUE' ? WarningAmberIcon : AssignmentIndIcon;
    const parts = [row.currentStageName || 'Done'];
    if (!row.redacted) {
        parts.push(row.currentStageAssignee ? shortName(row.currentStageAssignee) : 'Unassigned');
        if (row.dueDate) parts.push(`due ${formatDue(row.dueDate)}`);
    }
    const label = `${row.languageLabel}: ${parts.join(' · ')} (${STATUS_META[status]?.label || ''})`;
    return (
        <Tooltip title={label} arrow>
            <IconButton
                size="small"
                aria-label={`Task for ${label}`}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    openTask(row.id);
                }}
                sx={{ p: 0.25, ml: -0.25 }}
                color={status === 'BLOCKED' || status === 'OVERDUE' ? 'error' : status === 'DUE_SOON' ? 'warning' : 'primary'}
            >
                <Icon sx={{ fontSize: '0.95rem' }} />
            </IconButton>
        </Tooltip>
    );
};
